"use client";

import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { useJobContext } from "@/components/jobs/job-context";
import type { FitScoreBreakdown } from "@/lib/match/score";

// Mirrors score.ts's `STRONG_MATCH_FLOOR` (kept as a literal, not a value
// import, so this client component never pulls the scorer module — a
// server-only-adjacent, non-trivial module — into the browser bundle for one
// constant; `job-context.tsx` follows the same type-only-import discipline
// for everything else out of `@/lib/match/*`).
const STRONG_MATCH_FLOOR = 60;

type Props = {
	locale: "en" | "id";
};

/**
 * Explainable fit-score rubric badge (W4) — the breakdown behind
 * `JobMatchTeaser`'s single blended score, for signed-in users with at least
 * one Hunt signal source (`getFitScoreBreakdownForUser`). Renders nothing for
 * guests and for signed-in users the scorer has no signal for — a rubric with
 * zero real inputs would be as dishonest as a fabricated score, the same rule
 * `computeJobMatchTeaser` follows for `matchTeaser`.
 *
 * `fit_score_shown` telemetry is captured SERVER-SIDE, in
 * `/api/me/job-context` at the moment the breakdown is computed (whenever it
 * resolves non-null the badge below WILL render) — not here. This component
 * only reads the already-fetched `useJobContext()` value; it never re-fires
 * the capture on every client render/remount.
 *
 * IMPORTANT: this is a JOB-DETAIL-ONLY surface, exactly like `GeoFitBadge`
 * (see that file's own guardrail comment) — never mount this on the feed/list
 * row. A per-row breakdown for every card in a list is noise, not signal;
 * `src/lib/jobs/fit-blocker.ts` bans the geo badge there for the identical
 * reason and the same logic applies here.
 *
 * Mount exactly ONCE per job-detail page render, alongside `JobMatchTeaser`
 * (`src/app/[locale]/(public)/jobs/[slug]/page.tsx`) — this component fetches
 * nothing itself, it only reads `useJobContext()`, so a second mount would
 * just duplicate the DOM node, not the request.
 */
export function FitScoreBadge({ locale }: Props) {
	const { fitScoreBreakdown } = useJobContext();

	if (!fitScoreBreakdown) return null;

	const isEn = locale === "en";
	const { skillOverlap, salaryFit, seniorityMatch, redFlags } =
		fitScoreBreakdown;
	const isStrong = fitScoreBreakdown.score >= STRONG_MATCH_FLOOR;

	return (
		<div
			data-testid="fit-score-badge"
			data-fit-score={fitScoreBreakdown.score}
			className="space-y-3 rounded-xl border border-border/60 bg-card p-5"
		>
			<div className="flex items-center justify-between">
				<span className="text-sm font-semibold text-foreground">
					{isEn ? "Why this fits" : "Kenapa cocok"}
				</span>
				<span
					className={
						isStrong
							? "rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary"
							: "rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground"
					}
				>
					{isEn
						? `${Math.round(fitScoreBreakdown.score)}% match`
						: `Cocok ${Math.round(fitScoreBreakdown.score)}%`}
				</span>
			</div>

			<ul className="space-y-1.5 text-xs text-muted-foreground">
				<li className="flex items-start gap-2">
					<FitIcon ok={skillOverlap.matched.length > 0} />
					<span>
						{skillOverlap.matched.length + skillOverlap.missing.length > 0
							? isEn
								? `${skillOverlap.matched.length}/${skillOverlap.matched.length + skillOverlap.missing.length} skills matched`
								: `${skillOverlap.matched.length}/${skillOverlap.matched.length + skillOverlap.missing.length} skill cocok`
							: isEn
								? "No skills signal for this role"
								: "Belum ada sinyal skill untuk role ini"}
					</span>
				</li>
				<li className="flex items-start gap-2">
					<FitIcon ok={salaryFit === "met" || salaryFit === "close"} />
					<span>{salaryFitLabel(salaryFit, isEn)}</span>
				</li>
				<li className="flex items-start gap-2">
					<FitIcon ok={seniorityMatch > 0} />
					<span>
						{seniorityMatch > 0
							? isEn
								? "Seniority level fits your experience"
								: "Level seniority cocok dengan pengalamanmu"
							: isEn
								? "Seniority level may not fit your experience"
								: "Level seniority mungkin belum cocok dengan pengalamanmu"}
					</span>
				</li>
				{redFlags.length > 0 && (
					<li className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
						<AlertTriangle
							className="mt-0.5 h-3.5 w-3.5 shrink-0"
							aria-hidden="true"
						/>
						<span>
							{isEn ? "Flags: " : "Tanda: "}
							{redFlags.slice(0, 3).join(", ")}
						</span>
					</li>
				)}
			</ul>
		</div>
	);
}

function FitIcon({ ok }: { ok: boolean }) {
	return ok ? (
		<CheckCircle2
			className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
			aria-hidden="true"
		/>
	) : (
		<XCircle
			className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
			aria-hidden="true"
		/>
	);
}

function salaryFitLabel(
	salaryFit: FitScoreBreakdown["salaryFit"],
	isEn: boolean,
): string {
	switch (salaryFit) {
		case "met":
			return isEn ? "Meets your salary floor" : "Memenuhi target gajimu";
		case "close":
			return isEn ? "Close to your salary floor" : "Mendekati target gajimu";
		case "below":
		case "far-below":
			return isEn ? "Below your salary floor" : "Di bawah target gajimu";
		case "undisclosed":
			return isEn
				? "Salary not disclosed by employer"
				: "Gaji tidak dicantumkan employer";
		default:
			return isEn
				? "Set a salary floor in Hunt to compare"
				: "Atur target gaji di Hunt untuk membandingkan";
	}
}
